import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { Box, CircularProgress, Dialog, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";

import { colors, radius } from "../theme/tokens";

import { usePdfDocument, type PdfStatus } from "./use-pdf-document";

import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";

// A paginated PDF viewer: one page at a time, drawn to a canvas with pdf.js,
// with back / forward arrows, a page counter, and a full-screen mode (Bill,
// 2026-09-06: the stacked scroll was hard to use; step through the deck like
// slides instead). An <iframe src="x.pdf"> only shows the document when the
// browser's own PDF viewer is on; with it off (a common Chrome / Edge
// setting) or on a phone the frame turns into an open / download prompt, so
// the pages are drawn here. pdf.js is imported lazily: it costs the main
// bundle nothing until a modal with a document opens.
//
// Full screen is a MUI fullScreen Dialog rather than the Fullscreen API, so it
// behaves the same on iOS Safari (which has no element fullscreen) and closes
// with the usual X / Escape.

// Every embedded viewer carries a download button in the bar, left of full
// screen (2026-10-25 on Programs; every use since 2026-10-30, Elijah).
export function PdfPages({ url, title }: { url: string; title: string }) {
    const { doc, status } = usePdfDocument(url);
    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState(false);

    const viewer = (fit: "width" | "contain", onClose?: () => void) => (
        <PdfViewer
            doc={doc}
            status={status}
            page={page}
            onPage={setPage}
            title={title}
            fit={fit}
            onExpand={onClose ? undefined : () => setExpanded(true)}
            onClose={onClose}
            downloadUrl={onClose ? undefined : url}
        />
    );

    return (
        <>
            {viewer("width")}
            <Dialog open={expanded} fullScreen onClose={() => setExpanded(false)}>
                {viewer("contain", () => setExpanded(false))}
            </Dialog>
        </>
    );
}

// ─── One page + controls ─────────────────────────────────────────────

interface PdfViewerProps {
    doc: PDFDocumentProxy | null;
    status: PdfStatus;
    page: number;
    onPage: (page: number) => void;
    title: string;
    /** `width`: the page fills the box width (inline). `contain`: the page fits the box (full screen). */
    fit: "width" | "contain";
    onExpand?: () => void;
    onClose?: () => void;
    downloadUrl?: string;
}

function PdfViewer({ doc, status, page, onPage, title, fit, onExpand, onClose, downloadUrl }: PdfViewerProps) {
    const frameRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const taskRef = useRef<RenderTask | null>(null);
    const numPages = doc?.numPages ?? 0;
    const fullScreen = Boolean(onClose);

    // Draw the current page whenever the page, the document or the box size
    // changes. One render at a time per canvas: only the newest draw paints,
    // and it waits for the previous task to finish cancelling first. Two
    // overlapping renders on one canvas drew a second, upside-down copy of the
    // page (Bill, 2026-10-30, the COI sample's Edge-printed page 2).
    useEffect(() => {
        const frame = frameRef.current;
        if (!doc || !frame) return;
        let cancelled = false;
        let seq = 0;
        const draw = async () => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const id = ++seq;
            const p = await doc.getPage(page);
            if (cancelled || id !== seq) return;
            const previous = taskRef.current;
            if (previous) {
                previous.cancel();
                await previous.promise.catch(() => undefined);
                if (cancelled || id !== seq) return;
            }
            const base = p.getViewport({ scale: 1 });
            const w = frame.clientWidth;
            const h = frame.clientHeight;
            const scale =
                fit === "contain" && h > 0
                    ? Math.min(w / base.width, h / base.height)
                    : w / base.width;
            if (!(scale > 0)) return;
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const viewport = p.getViewport({ scale: scale * dpr });
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.style.width = `${viewport.width / dpr}px`;
            canvas.style.height = `${viewport.height / dpr}px`;
            const task = p.render({ canvas, viewport });
            taskRef.current = task;
            try {
                await task.promise;
            } catch {
                // Cancelled by a newer render; nothing to do.
            }
        };
        void draw();
        const observer = new ResizeObserver(() => void draw());
        observer.observe(frame);
        return () => {
            cancelled = true;
            observer.disconnect();
            taskRef.current?.cancel();
        };
    }, [doc, page, fit]);

    const prev = () => onPage(Math.max(1, page - 1));
    const next = () => onPage(Math.min(numPages, page + 1));

    return (
        <Box
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    prev();
                } else if (e.key === "ArrowRight") {
                    e.preventDefault();
                    next();
                }
            }}
            sx={{
                display: "flex",
                flexDirection: "column",
                outline: "none",
                ...(fullScreen
                    ? { height: "100%", bgcolor: colors.background }
                    : {
                          border: 1,
                          borderColor: "divider",
                          borderRadius: `${radius.lg}px`,
                          bgcolor: colors.lightGray,
                          overflow: "hidden",
                      }),
                "&:focus-visible": { boxShadow: `0 0 0 2px ${colors.navy}` },
            }}
        >
            {fullScreen && (
                <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, color: colors.navy, flex: 1 }} noWrap>
                        {title}
                    </Typography>
                    <IconButton onClick={onClose} aria-label="Exit full screen" size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            )}
            <Box
                ref={frameRef}
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: fullScreen ? "center" : "flex-start",
                    justifyContent: "center",
                    ...(fullScreen ? { flex: 1, minHeight: 0, px: 3 } : { minHeight: 160 }),
                }}
            >
                <canvas
                    ref={canvasRef}
                    role="img"
                    aria-label={numPages ? `${title}, page ${page} of ${numPages}` : title}
                    style={{ display: status === "ready" ? "block" : "none", maxWidth: "100%" }}
                />
                {status === "loading" && (
                    <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {status === "error" && (
                    <Typography variant="body2" sx={{ p: 3, color: "text.secondary" }}>
                        This document could not be displayed here. Use the link below to open it.
                    </Typography>
                )}
            </Box>
            {status === "ready" && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{
                        position: "relative",
                        px: 1.5,
                        py: 1,
                        borderTop: fullScreen ? 0 : 1,
                        borderColor: "divider",
                        bgcolor: fullScreen ? "transparent" : colors.white,
                    }}
                >
                    <IconButton onClick={prev} disabled={page <= 1} aria-label="Previous page" size="small">
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy, minWidth: 96, textAlign: "center" }}>
                        Page {page} of {numPages}
                    </Typography>
                    <IconButton onClick={next} disabled={page >= numPages} aria-label="Next page" size="small">
                        <ChevronRightIcon />
                    </IconButton>
                    {(onExpand || downloadUrl) && (
                        // Bottom-right corner of the viewer (Bill, 2026-09-06), off the
                        // centred paging controls; download sits left of full screen.
                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}
                        >
                            {downloadUrl && (
                                <IconButton
                                    component="a"
                                    href={downloadUrl}
                                    download
                                    aria-label={`Download ${title}`}
                                    title="Download"
                                    size="small"
                                >
                                    <FileDownloadOutlinedIcon />
                                </IconButton>
                            )}
                            {onExpand && (
                                <IconButton onClick={onExpand} aria-label="View full screen" title="Full screen" size="small">
                                    <FullscreenIcon />
                                </IconButton>
                            )}
                        </Stack>
                    )}
                </Stack>
            )}
        </Box>
    );
}
